
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eye } from "lucide-react";
import type { Json } from "@/integrations/supabase/types";

interface AssessmentHistory {
  id: string;
  session_id: string;
  questions_answers: Json;
  result: Json;
  completed_at: string;
}

interface HistoryDetailsProps {
  selectedHistory: AssessmentHistory | null;
}

const HistoryDetails = ({ selectedHistory }: HistoryDetailsProps) => {
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "emergency": return "destructive";
      case "urgent": return "secondary";
      case "routine": return "default";
      case "self-care": return "outline";
      default: return "outline";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getResultData = (result: Json) => {
    if (typeof result === 'object' && result !== null && !Array.isArray(result)) {
      return result as { urgency?: string; category?: string; recommendation?: string };
    }
    return { urgency: 'unknown', category: 'Unknown', recommendation: 'No recommendation available' };
  };

  const getAnswersData = (answers: Json) => {
    if (typeof answers === 'object' && answers !== null && !Array.isArray(answers)) {
      return answers as Record<string, any>;
    }
    return {};
  };

  return (
    <Card className="p-6 bg-white/80 backdrop-blur-sm border-blue-100">
      {selectedHistory ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Assessment Details</h2>
            <Badge variant={getUrgencyColor(getResultData(selectedHistory.result).urgency || 'unknown')}>
              {getResultData(selectedHistory.result).urgency || 'unknown'}
            </Badge>
          </div>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Assessment Result</h3>
              <p className="text-sm text-gray-700 mb-2">{getResultData(selectedHistory.result).recommendation || 'No recommendation available'}</p>
              <p className="text-sm text-gray-600">Category: {getResultData(selectedHistory.result).category || 'Unknown'}</p>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-2">Your Responses</h3>
              <div className="space-y-2">
                {Object.entries(getAnswersData(selectedHistory.questions_answers)).map(([key, value]) => (
                  <div key={key} className="text-sm">
                    <span className="font-medium text-gray-700 capitalize">
                      {key.replace(/_/g, ' ')}:
                    </span>
                    <span className="text-gray-600 ml-2">
                      {Array.isArray(value) ? value.join(', ') : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="text-xs text-gray-500">
                Completed: {formatDate(selectedHistory.completed_at)}
              </p>
              <p className="text-xs text-gray-500">
                Session ID: {selectedHistory.session_id}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Select an assessment to view details</p>
        </div>
      )}
    </Card>
  );
};

export default HistoryDetails;
